import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {useStore} from 'effector-react';

import {useTranslation} from 'react-i18next';

import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {CheckboxArray} from 'components/ui-kit/CheckboxArray';
import {Modal} from 'components/ui-kit/Modal';
import {$allowedActionsForInvoice} from 'models/documents/invoices/common';
import {
    $invoiceLinesHolder,
    removeInvoiceLinesHolder,
    setInvoiceLinesHolder,
    updateInvoiceLinesHolder,
} from 'models/documents/invoices/create';
import {getRowAtPath} from 'models/documents/invoices/create/init';
import {
    $modalGroupByGtinSettings,
    setModalGroupByGtinSettings,
} from 'models/documents/invoices/details';

import {TRANSPORT_PACKAGE_TYPES} from 'types/gtins';

import {Footer, Headline, List, ListItem, Root, SelectedCount} from './styled';

export const ModalGroupByGtin = () => {
    const {t: translation} = useTranslation();
    const GEN_INFO = 'pages.documents.invoices.create.grouping';
    const t = (key: string) => translation(`${GEN_INFO}.${key}`);
    const allowedActionsForInvoice = useStore($allowedActionsForInvoice);
    const modalGroupByGtinSettings = useStore($modalGroupByGtinSettings);
    const invoiceLinesHolder = useStore($invoiceLinesHolder);
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const onCloseModal = () => {
        setModalGroupByGtinSettings({
            lineIndex: '-1',
            show: false,
            gtin: undefined,
            packagingType: undefined,
        });
    };

    const allowToDelete = useMemo(
        () =>
            allowedActionsForInvoice.edit &&
            modalGroupByGtinSettings.lineIndex.split('.').length <= 1,
        [modalGroupByGtinSettings, allowedActionsForInvoice.edit],
    );

    const options = useMemo(() => {
        if (!modalGroupByGtinSettings.show) {
            return [];
        }

        // const combinedCodes = invoiceLinesHolder[modalGroupByGtinSettings.lineIndex];
        const combinedCodes = getRowAtPath(invoiceLinesHolder, modalGroupByGtinSettings.lineIndex);
        return (
            combinedCodes?.combinedCodes?.map((i, index) => ({
                label: i,
                value: i,
            })) ?? []
        );
    }, [invoiceLinesHolder, modalGroupByGtinSettings.lineIndex, modalGroupByGtinSettings.show]);

    const onDelete = useCallback(() => {
        const item = getRowAtPath(invoiceLinesHolder, modalGroupByGtinSettings.lineIndex);
        if (!item) {
            return;
        }
        const {combinedCodes, combinedCodeId, ...rest} = item;
        const toLowerAndTrim = (str: string) => str.toLowerCase().trim();
        if (combinedCodes) {
            const updatedCodes = combinedCodes.filter((code) => !selectedValues.includes(code));

            const addCharges = rest['charges']
                ? toLowerAndTrim(rest['charges'].isCharge) === 'true'
                    ? Number(rest.charges.amountXaf)
                    : -Number(rest.charges.amountXaf)
                : 0;

            const qty = updatedCodes.length;
            const lines = (qty: number) => qty * Number(rest['price']) + addCharges;
            const lineAmountWVat = (qty: number) =>
                (Number(rest.vat) / 100) * lines(qty) + lines(qty);

            if (updatedCodes.length < 2) {
                removeInvoiceLinesHolder(modalGroupByGtinSettings.lineIndex.toString());

                updatedCodes.length === 1 &&
                    setInvoiceLinesHolder([
                        {
                            ...rest,
                            code: updatedCodes[0],
                            qty: '1',
                            lines: !rest['price'] || !qty ? '' : lines(1).toFixed(2),
                            lineAmountWVat:
                                !rest['price'] || !qty ? '' : lineAmountWVat(1).toFixed(2),
                        },
                    ]);
            } else {
                updateInvoiceLinesHolder({
                    // index: modalGroupByGtinSettings.lineIndex,
                    depth: {step: 0, id: modalGroupByGtinSettings.lineIndex.toString()},
                    data: [
                        {
                            columnId: 'combinedCodes',
                            value: updatedCodes,
                        },
                        {
                            columnId: 'qty',
                            value: qty.toString(),
                        },
                        {
                            columnId: 'lines',
                            value: !rest['price'] || !qty ? '' : lines(qty).toFixed(2),
                        },
                        {
                            columnId: 'lineAmountWVat',
                            value: !rest['price'] || !qty ? '' : lineAmountWVat(qty).toFixed(2),
                        },
                    ],
                });
            }

            // const mappedSelectedValues = selectedValues.map((code) => ({
            //     ...rest,
            //     code,
            //     qty: '1',
            //     lines: lines ? lines(1).toFixed(2) : '',
            //     lineAmountWVat: lineAmountWVat ? lineAmountWVat(1).toFixed(2) : '',
            // }));
            // setInvoiceLinesHolder(mappedSelectedValues);

            setSelectedValues([]);

            if (updatedCodes.length < 2) {
                onCloseModal();
            }
            // else {
            //     setModalGroupByGtinSettings({
            //         ...modalGroupByGtinSettings,
            //         lineIndex: modalGroupByGtinSettings.lineIndex + mappedSelectedValues.length,
            //     });
            // }
        }
    }, [invoiceLinesHolder, modalGroupByGtinSettings.lineIndex, selectedValues]);

    return (
        <Modal
            show={modalGroupByGtinSettings.show}
            onClose={onCloseModal}
            title="Consumer codes"
            modalWidth={0}
        >
            {modalGroupByGtinSettings.show && (
                <Root>
                    {allowToDelete && <Headline>{t('deleteFromGroup')}</Headline>}
                    {allowToDelete ? (
                        <CheckboxArray
                            searchable={true}
                            name="combinedCodes"
                            options={options}
                            onChange={(values) => setSelectedValues(values ?? [])}
                        />
                    ) : (
                        <List>
                            {options.map((option) => (
                                <ListItem key={option.value}>{option.label}</ListItem>
                            ))}
                        </List>
                    )}
                    {allowToDelete && (
                        <Footer>
                            <SelectedCount>
                                {selectedValues.length} {t('selected')}
                            </SelectedCount>
                            <Button
                                type="button"
                                style={{width: 'fit-content', color: 'red', borderColor: 'red'}}
                                size="md"
                                variant={ButtonVariant.Outline}
                                onClick={onDelete}
                            >
                                {t('delete')}
                            </Button>
                        </Footer>
                    )}
                </Root>
            )}
        </Modal>
    );
};
